/**
 * Utility functions for finding elements in the template tree
 */
import type { ContainerElement, TemplateElement, AbsoluteElement } from '../types/template.types'

// Find selected element in container tree (returns parent container)
export const findElementById = (
  container: ContainerElement,
  id: string
): ContainerElement | null => {
  if (container.id === id) return container

  const children = Array.isArray(container.children) ? container.children : []
  for (const child of children) {
    if (child.element.type === 'container') {
      const found = findElementById(child.element as ContainerElement, id)
      if (found) return found
    } else if (child.element.id === id) {
      return container // Return parent container for content elements
    }
  }
  return null
}

// Find any element (container or content) by ID
export const findAnyElementById = (
  container: ContainerElement,
  id: string
): TemplateElement | null => {
  if (container.id === id) return container

  const children = Array.isArray(container.children) ? container.children : []
  for (const child of children) {
    if (child.element.id === id) {
      return child.element
    }
    if (child.element.type === 'container') {
      const found = findAnyElementById(child.element as ContainerElement, id)
      if (found) return found
    }
  }
  return null
}

// Helper to search in array of containers
export const findElementByIdInArray = (
  containers: ContainerElement[],
  id: string
): ContainerElement | null => {
  for (const container of containers) {
    const found = findElementById(container, id)
    if (found) return found
  }
  return null
}

export const findAnyElementByIdInArray = (
  containers: ContainerElement[],
  id: string
): TemplateElement | null => {
  for (const container of containers) {
    const found = findAnyElementById(container, id)
    if (found) return found
  }
  return null
}

// Find parent container of an element and return its height (if fixed)
export const getParentContainerMaxHeight = (
  elementId: string,
  headerContainer: ContainerElement,
  footerContainer: ContainerElement,
  rootContainer: ContainerElement | ContainerElement[] | null,
  topMargin: number,
  bottomMargin: number
): number | undefined => {
  const findInContainer = (container: ContainerElement): number | undefined => {
    const children = Array.isArray(container.children) ? container.children : []
    for (const child of children) {
      if (child.element.id === elementId) {
        // Found element - return container height if it's fixed
        return container.height
      }
      if (child.element.type === 'container') {
        const found = findInContainer(child.element as ContainerElement)
        if (found !== undefined) return found
      }
    }
    return undefined
  }

  // Check header container
  if (findAnyElementById(headerContainer, elementId)) {
    return topMargin // Header always uses topMargin
  }

  // Check footer container
  if (findAnyElementById(footerContainer, elementId)) {
    return bottomMargin // Footer always uses bottomMargin
  }

  // Check root containers
  if (Array.isArray(rootContainer)) {
    for (const container of rootContainer) {
      const height = findInContainer(container)
      if (height !== undefined) return height
    }
  } else if (rootContainer) {
    return findInContainer(rootContainer)
  }

  return undefined
}

// Get selected container
export const getSelectedContainer = (
  selectedElementId: string | null,
  headerContainer: ContainerElement,
  footerContainer: ContainerElement,
  rootContainer: ContainerElement | ContainerElement[] | null
): ContainerElement | null => {
  if (!selectedElementId) return null

  // Check if it's the header or footer container itself
  if (selectedElementId === headerContainer.id) return headerContainer
  if (selectedElementId === footerContainer.id) return footerContainer

  // Check in header container children
  const inHeader = findElementById(headerContainer, selectedElementId)
  if (inHeader) return inHeader

  // Check in footer container children
  const inFooter = findElementById(footerContainer, selectedElementId)
  if (inFooter) return inFooter

  // Check in root containers
  if (rootContainer) {
    return Array.isArray(rootContainer)
      ? findElementByIdInArray(rootContainer, selectedElementId)
      : findElementById(rootContainer, selectedElementId)
  }
  return null
}

// Get selected element
export const getSelectedElement = (
  selectedElementId: string | null,
  headerContainer: ContainerElement,
  footerContainer: ContainerElement,
  rootContainer: ContainerElement | ContainerElement[] | null,
  absoluteElements: AbsoluteElement[]
): TemplateElement | AbsoluteElement | null => {
  if (!selectedElementId) return null

  // Check if it's the header or footer container itself
  if (selectedElementId === headerContainer.id) return headerContainer
  if (selectedElementId === footerContainer.id) return footerContainer

  // Check in header container
  const inHeader = findAnyElementById(headerContainer, selectedElementId)
  if (inHeader) return inHeader

  // Check in footer container
  const inFooter = findAnyElementById(footerContainer, selectedElementId)
  if (inFooter) return inFooter

  // Check if it's an absolute element
  const absoluteElement = absoluteElements.find(el => el.id === selectedElementId)
  if (absoluteElement) return absoluteElement

  // Otherwise search in root containers
  if (rootContainer) {
    return Array.isArray(rootContainer)
      ? findAnyElementByIdInArray(rootContainer, selectedElementId)
      : findAnyElementById(rootContainer, selectedElementId)
  }
  return null
}
